import { useEffect, useRef, useState } from 'react'

import { MatchingPairsQuestion } from '~/types/bootcamp'
import { BlockContent } from '~/components'

import { displayedPairKeys, IndividualMathOBJ, shuffleArray } from '../utils/MatchingPairs.utils'
import { getData } from '../utils/data.matchingPairs'
import { AnswerType } from '../MatchingPairs.type'

export default function MatchingPairsApp({
  question,
  setFeedBackMessage,
  onDisplayedPairsChange,
}: {
  question: MatchingPairsQuestion
  setFeedBackMessage: (feedback: AnswerType | null) => void
  onDisplayedPairsChange?: (keys: string[] | undefined) => void
}): React.ReactElement {
  const [isSelected, setIsSelected] = useState<IndividualMathOBJ[] | undefined>([])
  const wrongReportedRef = useRef(false)
  useEffect(() => {
    wrongReportedRef.current = false
    const data = getData([question])
    const shuffled = shuffleArray(data, 3, false)
    onDisplayedPairsChange?.(displayedPairKeys(shuffled))
    setIsSelected(
      shuffled.map((obj: IndividualMathOBJ) => ({
        ...obj,
        isCorrect: false,
        isClicked: false,
        isWrong: false,
      })),
    )
  }, [question, onDisplayedPairsChange])

  const classes = {
    clicked: 'bg-blue-blueberry border-blue',
    default: 'bg-gradient-purple-dark-top border-purple',
    wrong: 'bg-red border-red-raspberry pointer-events-none bg-red',
    correct: 'bg-green-checkmark pointer-events-none border-green-dark',
    start:
      'min-w-30 max-w-full text-white rounded-2 flex justify-center items-center flex-col text-center border-4 cursor-pointer transition-transform hover:scale-105 hover:-translate-y-1',
  }

  function getNextState(obj: IndividualMathOBJ, prevState: IndividualMathOBJ[]): IndividualMathOBJ[] | undefined {
    // selecting the chosen div/card
    const existingClicked = prevState!.find((existingObj) => existingObj.isClicked)
    // checks if the user has clicked the same card twice
    if (existingClicked?.key === obj.key) {
      return prevState!.map((currentObj) => {
        if (currentObj.key === obj.key) {
          return { ...currentObj, isClicked: false }
        } else {
          return currentObj
        }
      })
    }
    let nextState = prevState
    // Checks to see if the user has clicked a second card, and that it is not the same card
    if (existingClicked) {
      const isSamePair = obj.pairID === existingClicked.pairID
      // Checks to see if this is a pair
      if (isSamePair) {
        const object = prevState.map((currentObj) => {
          if (currentObj.pairID === obj.pairID) {
            return { ...currentObj, isCorrect: true, isClicked: false }
          } else {
            return currentObj
          }
        })

        nextState = object
      } else {
        const updatedList = prevState.map((currentObj) => {
          if (currentObj.isClicked || currentObj.key === obj.key) {
            return { ...currentObj, isClicked: false, isWrong: true }
          } else {
            return currentObj
          }
        })
        // One wrong answer per board, like a single wrong Check on other types.
        if (!wrongReportedRef.current) {
          wrongReportedRef.current = true
          setFeedBackMessage(AnswerType.WRONG)
        }
        setTimeout(() => {
          const updatedListWithoutWrong = updatedList.map((incorrectObj) => ({
            ...incorrectObj,
            isWrong: false,
          }))
          setIsSelected(updatedListWithoutWrong)
        }, 500) // 500 milliseconds = 0.5 seconds

        nextState = updatedList
      }
      if (nextState.every((item) => item.isCorrect)) {
        setFeedBackMessage(AnswerType.CORRECT)
      }
    }

    // if nothing has been true up to this point, that means that there is no existing card and this will be the first active pick.
    else {
      return prevState.map((currentObj) => {
        if (currentObj.key === obj.key) {
          return { ...currentObj, isClicked: true }
        } else {
          return currentObj
        }
      })
    }

    return nextState
  }

  function clickHandler(obj: IndividualMathOBJ): void {
    if (!isSelected) return
    const nextState = getNextState(obj, isSelected)
    setIsSelected(nextState)
  }

  function getReleventClass(obj: IndividualMathOBJ): string {
    if (obj.isClicked) {
      return classes.clicked
    } else if (obj.isCorrect) {
      return classes.correct
    } else if (obj.isWrong) {
      return classes.wrong
    } else {
      return classes.default
    }
  }

  return (
    <div>
      <div className="grid grid-cols-2 gap-4">
        {isSelected!.map((obj) => (
          <div
            className={`${classes.start} ${getReleventClass(obj)} min-h-16`}
            key={obj.key}
            onClick={() => clickHandler(obj)}
          >
            <BlockContent blocks={obj.value} />
          </div>
        ))}
      </div>
    </div>
  )
}
